import { Label } from "@/ui/primitives/label";
import Button from "@imminently/decisively-ui/Button";
import { TIME_PRESETS, type TimePreset } from "@imminently/decisively-ui/TimeInput";
import * as React from "react";
import { ComponentPreview } from "./ComponentPage";

export type TestInput = { input: string; expect: "accept" | "reject"; note?: string };

interface DateTimeTestInputsProps {
  description: string;
  inputs: TestInput[];
  /** What the component's parser makes of the input, or null when it is rejected. */
  describe: (input: string) => string | null;
}

/** Strings from the ENG-240 risk check, with what the parser does with each one. */
export function DateTimeTestInputs({ description, inputs, describe }: DateTimeTestInputsProps) {
  return (
    <ComponentPreview className="flex-col items-stretch justify-start gap-4">
      <div>
        <Label>Test inputs</Label>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="grid grid-cols-[10rem_5rem_1fr_auto] items-center gap-x-4 gap-y-1 text-sm">
        <span className="text-xs font-medium text-muted-foreground">Input</span>
        <span className="text-xs font-medium text-muted-foreground">Expected</span>
        <span className="text-xs font-medium text-muted-foreground">Parser result</span>
        <span />
        {inputs.map(({ input, expect, note }) => {
          const result = describe(input);
          const matches = (result !== null) === (expect === "accept");
          return (
            <React.Fragment key={input}>
              <code>{input}</code>
              <span className="text-muted-foreground">{expect}</span>
              <span className={matches ? "text-muted-foreground" : "text-destructive"}>
                {result ?? "rejected"}
                {note ? <span className="text-xs"> ({note})</span> : null}
              </span>
              <Button
                size="sm"
                variant="ghost"
                label={`Copy ${input}`}
                onClick={() => navigator.clipboard?.writeText(input)}
              >
                Copy
              </Button>
            </React.Fragment>
          );
        })}
      </div>
    </ComponentPreview>
  );
}

interface KeyboardReferenceProps {
  rows: [keys: string, action: string][];
}

export function KeyboardReference({ rows }: KeyboardReferenceProps) {
  return (
    <ComponentPreview className="items-stretch justify-start">
      <dl className="grid grid-cols-[10rem_1fr] gap-x-4 gap-y-2 text-sm">
        {rows.map(([keys, action]) => (
          <React.Fragment key={keys}>
            <dt>
              <code>{keys}</code>
            </dt>
            <dd className="text-muted-foreground">{action}</dd>
          </React.Fragment>
        ))}
      </dl>
    </ComponentPreview>
  );
}

export const LOCALE_OPTIONS = [
  { value: "browser", label: "Browser default" },
  { value: "en-AU", label: "en-AU (day first)" },
  { value: "en-US", label: "en-US (month first)" },
  { value: "sv-SE", label: "sv-SE (ISO)" },
];

export const resolveLocale = (value: string) => (value === "browser" ? undefined : value);

/** Presets are consumer-defined; TIME_PRESETS is only a set of ready-made ones. */
export const CUSTOM_PRESETS: TimePreset[] = [
  { label: "Lunch", time: "12:30pm" },
  { label: "Close of business", time: "17:30" },
];

export const PRESET_SETS: Record<string, { label: string; presets?: TimePreset[] }> = {
  none: { label: "None" },
  dayBounds: { label: "Start / end of day", presets: [TIME_PRESETS.startOfDay, TIME_PRESETS.endOfDay] },
  businessHours: {
    label: "9am / midday / 5pm",
    presets: [TIME_PRESETS.nineAm, TIME_PRESETS.midday, TIME_PRESETS.fivePm],
  },
  all: { label: "All TIME_PRESETS", presets: Object.values(TIME_PRESETS) },
  custom: { label: "Custom (consumer-defined)", presets: CUSTOM_PRESETS },
};

export const PRESET_SET_OPTIONS = Object.entries(PRESET_SETS).map(([value, { label }]) => ({ value, label }));

export const PRESET_LAYOUT_OPTIONS = [
  { value: "side", label: "Beside the times" },
  { value: "stacked", label: "Above the times" },
];

export const STEP_OPTIONS = [
  { value: "none", label: "None (presets only)" },
  ...[5, 10, 15, 30, 60].map((minutes) => ({ value: String(minutes), label: `${minutes} min` })),
];

export const resolveStep = (value: string) => (value === "none" ? null : Number(value));
